import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { VdpCardType } from '~/constants/home/VdpCardType';

type GlobalContextType = {
  displayLowerFoldOnNative: boolean;
  isReturningUser: boolean;
  isVdpLinksLoaded: boolean;
  refreshSrpLinksTrigger: number;
  setDisplayLowerFoldOnNative: React.Dispatch<React.SetStateAction<boolean>>;
  setIsVdpLinksLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setVdpCardTypeShown: React.Dispatch<React.SetStateAction<VdpCardType>>;
  triggerRefreshSrpCard: () => void;
  vdpCardTypeShown: VdpCardType;
};

export const defaultGlobalContext: GlobalContextType = {
  displayLowerFoldOnNative: true,
  isReturningUser: false,
  isVdpLinksLoaded: false,
  refreshSrpLinksTrigger: 0,
  setDisplayLowerFoldOnNative: () => '',
  setIsVdpLinksLoaded: () => '',
  setVdpCardTypeShown: () => '',
  triggerRefreshSrpCard: () => {},
  vdpCardTypeShown: VdpCardType.None
};

export type OwnProps = React.PropsWithChildren<{
  displayLowerFoldOnNative: boolean;
  isReturningUser: boolean;
}>;

const GlobalContext = createContext<GlobalContextType>(defaultGlobalContext);

export function GlobalContextProvider({ children, ...props }: OwnProps) {
  const [refreshSrpLinksTrigger, setRefreshSrpLinksTrigger] = useState(
    defaultGlobalContext.refreshSrpLinksTrigger
  );
  const [vdpCardTypeShown, setVdpCardTypeShown] = useState(
    defaultGlobalContext.vdpCardTypeShown
  );
  const [isVdpLinksLoaded, setIsVdpLinksLoaded] = useState(
    defaultGlobalContext.isVdpLinksLoaded
  );

  const triggerRefreshSrpCard = useCallback(() => {
    setRefreshSrpLinksTrigger(Date.now());
  }, []);

  const [displayLowerFoldOnNative, setDisplayLowerFoldOnNative] = useState(
    props.displayLowerFoldOnNative
  );

  const value = useMemo<GlobalContextType>(
    () => ({
      displayLowerFoldOnNative,
      isReturningUser: props.isReturningUser,
      isVdpLinksLoaded,
      refreshSrpLinksTrigger,
      setDisplayLowerFoldOnNative,
      setIsVdpLinksLoaded,
      setVdpCardTypeShown,
      triggerRefreshSrpCard,
      vdpCardTypeShown
    }),
    [
      displayLowerFoldOnNative,
      isVdpLinksLoaded,
      props.isReturningUser,
      refreshSrpLinksTrigger,
      triggerRefreshSrpCard,
      vdpCardTypeShown
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.displayName = 'GlobalContextProvider';

export function useGetIsReturnUser() {
  return useContext(GlobalContext).isReturningUser;
}

export default GlobalContext;
